/**
 * tracking.ts — Package tracking board interactions — Phase 196
 *
 * Paste bar: POST code/label/merchant (+ carrier if picker shown) to data-add-url
 * (handleAddTracking signal). On response:
 *   - {duplicate:true, id}    → scroll-to + highlight existing parcel (D-08)
 *   - {needsCarrier:true}     → reveal carrier picker, re-POST with chosen int carrier (D-07)
 *   - {id, duplicate:false}   → reload the board to show new parcel
 *   - {error/message}         → show status message inline
 *
 * Accordion: Bootstrap Collapse for per-parcel event history (data-bs-toggle already in Latte).
 * Module initialises on the /tracking board page (#tracking-board) AND the /tracking/<id>
 * detail page (#tracking-detail) — the link-transaction picker (Plan 04) lives on both.
 *
 * Link-transaction picker (D-09/D-10/D-11, Plan 04): a single shared Bootstrap Modal
 * (#tracking-link-modal) populated via event delegation on document.body for
 * .js-link-transaction triggers. Fetches candidates + currently-linked transactions from
 * data-candidates-url (handleLinkCandidates), POSTs link/unlink to data-link-url /
 * data-unlink-url. Search box debounced 300ms. Candidate/linked row text is HTML-escaped
 * before innerHTML insertion (T-196-15 — transaction name is user-controlled bank data).
 *
 * Bootstrap is NOT on window — import explicitly.
 * All signal URLs come from data-* attributes set in Latte ({link addTracking!}, etc).
 */

import { Collapse, Modal } from 'bootstrap';

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export default {

    _inited: false,

    // ── Link-transaction picker state (set on modal open, Plan 04) ─────────────
    _linkModalInstance: null as InstanceType<typeof Modal> | null,
    _linkTrackingId: '',
    _linkCandidatesUrl: '',
    _linkUrl: '',
    _linkUnlinkUrl: '',
    _linkSearchDebounce: null as ReturnType<typeof setTimeout> | null,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice
        if (this._inited) return;

        // Activate on the board page OR the detail page (link picker lives on both, Plan 04)
        const board = document.getElementById('tracking-board');
        const detail = document.getElementById('tracking-detail');
        if (!board && !detail) return;

        this._inited = true;

        if (board) {
            const addUrl = board.dataset.addUrl ?? '';

            // ── Paste bar form submit ───────────────────────────────────────────
            const form = document.getElementById('tracking-paste-form') as HTMLFormElement | null;
            if (form) {
                form.addEventListener('submit', (e: Event) => {
                    e.preventDefault();
                    this._submitAdd(addUrl, null);
                });
            }

            // ── Carrier picker: submit with chosen carrier ──────────────────────
            const carrierSubmit = document.getElementById('tracking-carrier-submit');
            if (carrierSubmit) {
                carrierSubmit.addEventListener('click', () => {
                    const select = document.getElementById('tracking-carrier-select') as HTMLSelectElement | null;
                    const carrier = select?.value ?? '';
                    if (!carrier) {
                        this._showStatus('Please select a carrier.', 'text-warning');
                        return;
                    }
                    this._submitAdd(addUrl, carrier);
                });
            }

            // ── Carrier picker: cancel ──────────────────────────────────────────
            const carrierCancel = document.getElementById('tracking-carrier-cancel');
            if (carrierCancel) {
                carrierCancel.addEventListener('click', () => {
                    this._hideCarrierPicker();
                });
            }

            // Initialise any Collapse elements already on the page (Bootstrap needs these for
            // toggle behaviour; data-bs-toggle handles it natively, but explicit init ensures
            // no missed elements on reload-without-navigate scenarios).
            board.querySelectorAll('.accordion-collapse').forEach((el: Element) => {
                new Collapse(el, { toggle: false });
            });
        }

        // Link-transaction picker — active on both board + detail pages (Plan 04)
        this._initLinkPicker();
    },

    // ── Link-transaction picker (D-09/D-10/D-11, Plan 04) ──────────────────────

    _initLinkPicker(): void {
        const modalEl = document.getElementById('tracking-link-modal');
        if (!modalEl) return;

        const searchInput = document.getElementById('tracking-link-search') as HTMLInputElement | null;
        const resultsEl   = document.getElementById('tracking-link-results');
        const currentEl   = document.getElementById('tracking-link-current');

        // Open the picker — event delegation so it works for every card's button
        document.body.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-link-transaction');
            if (btn) {
                e.preventDefault();
                this._linkTrackingId    = btn.dataset.trackingId ?? '';
                this._linkCandidatesUrl = btn.dataset.candidatesUrl ?? '';
                this._linkUrl           = btn.dataset.linkUrl ?? '';
                this._linkUnlinkUrl     = btn.dataset.unlinkUrl ?? '';

                if (searchInput) searchInput.value = '';
                if (resultsEl) resultsEl.innerHTML = '<p class="small text-muted mb-0">Loading candidates…</p>';
                if (currentEl) currentEl.innerHTML = '';

                if (!this._linkModalInstance) {
                    this._linkModalInstance = new Modal(modalEl);
                }
                this._linkModalInstance.show();

                this._fetchLinkCandidates('');
                return;
            }

            // Unlink control inside the "currently linked" list
            const unlinkBtn = (e.target as HTMLElement).closest<HTMLElement>('.js-unlink-transaction');
            if (unlinkBtn) {
                e.preventDefault();
                const transactionId = unlinkBtn.dataset.transactionId ?? '';
                this._postLinkAction(this._linkUnlinkUrl, transactionId, () => {
                    this._fetchLinkCandidates(searchInput?.value.trim() ?? '');
                });
                return;
            }

            // Candidate row click → link it
            const candidateRow = (e.target as HTMLElement).closest<HTMLElement>('.js-candidate-row');
            if (candidateRow) {
                e.preventDefault();
                const transactionId = candidateRow.dataset.transactionId ?? '';
                this._postLinkAction(this._linkUrl, transactionId, () => {
                    this._fetchLinkCandidates(searchInput?.value.trim() ?? '');
                });
            }
        });

        // Debounced search box → re-fetch candidates
        if (searchInput) {
            searchInput.addEventListener('input', () => {
                if (this._linkSearchDebounce) clearTimeout(this._linkSearchDebounce);
                this._linkSearchDebounce = setTimeout(() => {
                    this._fetchLinkCandidates(searchInput.value.trim());
                }, 300);
            });
        }
    },

    _fetchLinkCandidates(search: string): void {
        const resultsEl = document.getElementById('tracking-link-results');
        const currentEl = document.getElementById('tracking-link-current');
        if (!this._linkCandidatesUrl || !this._linkTrackingId) return;

        const body = new URLSearchParams();
        body.set('trackingId', this._linkTrackingId);
        if (search) body.set('search', search);

        fetch(this._linkCandidatesUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            this._renderLinkedList(currentEl, data.linked ?? []);
            this._renderCandidateList(resultsEl, data.candidates ?? []);
        })
        .catch(() => {
            if (resultsEl) resultsEl.innerHTML = '<p class="small text-danger mb-0">Failed to load candidates. Please try again.</p>';
        });
    },

    _renderLinkedList(container: HTMLElement | null, linked: any[]): void {
        if (!container) return;
        if (!linked.length) {
            container.innerHTML = '';
            return;
        }
        const rows = linked.map((t: any) => {
            const name   = escapeHtml(String(t.name ?? ''));
            const amount = escapeHtml(String(t.amount ?? ''));
            const date   = escapeHtml(String(t.date ?? ''));
            const id     = Number(t.id) || 0;
            return '<div class="list-group-item d-flex justify-content-between align-items-center py-2">'
                 + '<span><span class="badge text-bg-success me-2">Linked</span>' + name
                 + '<div class="text-muted small">' + date + ' · $' + amount + '</div></span>'
                 + '<button type="button" class="btn btn-sm btn-outline-danger js-unlink-transaction" '
                 + 'data-transaction-id="' + id + '" style="min-height:44px">Unlink</button>'
                 + '</div>';
        }).join('');
        container.innerHTML = '<p class="small fw-semibold mb-1">Currently linked</p><div class="list-group mb-2">' + rows + '</div>';
    },

    _renderCandidateList(container: HTMLElement | null, candidates: any[]): void {
        if (!container) return;
        if (!candidates.length) {
            container.innerHTML = '<p class="small text-muted mb-0">No candidate transactions found.</p>';
            return;
        }
        const rows = candidates.map((c: any) => {
            const name   = escapeHtml(String(c.name ?? ''));
            const amount = escapeHtml(String(c.amount ?? ''));
            const date   = escapeHtml(String(c.date ?? ''));
            const id     = Number(c.id) || 0;
            return '<button type="button" class="list-group-item list-group-item-action js-candidate-row '
                 + 'd-flex justify-content-between align-items-center" data-transaction-id="' + id
                 + '" style="min-height:44px"><span>' + name
                 + '<div class="text-muted small">' + date + '</div></span>'
                 + '<span class="fw-semibold">$' + amount + '</span></button>';
        }).join('');
        container.innerHTML = '<p class="small fw-semibold mb-1">Recent candidates</p><div class="list-group">' + rows + '</div>';
    },

    _postLinkAction(url: string, transactionId: string, onDone: () => void): void {
        if (!url || !transactionId || !this._linkTrackingId) return;

        const body = new URLSearchParams();
        body.set('trackingId', this._linkTrackingId);
        body.set('transactionId', transactionId);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then(() => {
            // Idempotent — refresh the picker's lists regardless of exact response shape
            onDone();
        })
        .catch(() => {
            // Silent-fail on network error (idempotent retry via re-opening the picker)
        });
    },

    // ── Add tracking code (paste bar POST) ────────────────────────────────────

    _submitAdd(addUrl: string, carrier: string | null): void {
        const codeInput     = document.getElementById('tracking-code-input') as HTMLInputElement | null;
        const labelInput    = document.getElementById('tracking-label-input') as HTMLInputElement | null;
        const merchantInput = document.getElementById('tracking-merchant-input') as HTMLInputElement | null;

        const code     = codeInput?.value.trim() ?? '';
        const label    = labelInput?.value.trim() ?? '';
        const merchant = merchantInput?.value.trim() ?? '';

        if (!code) {
            this._showStatus('Please enter a tracking code.', 'text-warning');
            return;
        }
        if (!addUrl) {
            this._showStatus('Add URL missing — page reload required.', 'text-danger');
            return;
        }

        const body = new URLSearchParams();
        body.set('code', code);
        if (label)    body.set('label', label);
        if (merchant) body.set('merchant', merchant);
        if (carrier)  body.set('carrier', carrier);

        this._showStatus('Adding…', 'text-muted');

        fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.duplicate && data.id) {
                // D-08: scroll to + highlight existing parcel
                this._hideCarrierPicker();
                this._hideStatus();
                this._scrollToParcel(data.id);
                return;
            }
            if (data.needsCarrier) {
                // D-07: carrier picker
                this._showStatus('', 'text-muted');
                this._showCarrierPicker();
                return;
            }
            if (data.error || data.message) {
                const msg = data.message ?? data.error ?? 'Unknown error';
                this._showStatus('Error: ' + msg, 'text-danger');
                return;
            }
            if (data.id) {
                // Success — reload board to show new parcel
                this._hideCarrierPicker();
                this._hideStatus();
                window.location.reload();
                return;
            }
            this._showStatus('Unexpected response. Please try again.', 'text-warning');
        })
        .catch((err: any) => {
            this._showStatus('Request failed. Please check your connection.', 'text-danger');
        });
    },

    // ── D-08: scroll to + briefly highlight existing parcel ───────────────────

    _scrollToParcel(id: number | string): void {
        const el = document.getElementById('parcel-' + id);
        if (!el) {
            // Parcel may be in delivered table or not visible; just reload
            window.location.reload();
            return;
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'outline 0s, outline-offset 0s';
        el.style.outline = '2px solid var(--ksat-accent)';
        el.style.outlineOffset = '2px';
        setTimeout(() => {
            el.style.outline = '';
            el.style.outlineOffset = '';
        }, 2500);
    },

    // ── D-07: carrier picker helpers ─────────────────────────────────────────

    _showCarrierPicker(): void {
        const picker = document.getElementById('tracking-carrier-picker');
        if (picker) picker.classList.remove('d-none');
    },

    _hideCarrierPicker(): void {
        const picker = document.getElementById('tracking-carrier-picker');
        if (picker) picker.classList.add('d-none');
    },

    // ── Status message helpers ────────────────────────────────────────────────

    _showStatus(msg: string, cls: string): void {
        const el = document.getElementById('tracking-status-msg');
        if (!el) return;
        el.className = 'mt-2 small ' + cls;
        el.textContent = msg;
        el.classList.remove('d-none');
    },

    _hideStatus(): void {
        const el = document.getElementById('tracking-status-msg');
        if (el) el.classList.add('d-none');
    },
};
